import { ExternalLink, Loader, Paperclip } from 'lucide-react'
import { EmptyState } from '@/components/ui'
import { sanitizeEmailHtml } from '@/lib/utils/sanitize-html'
import { maildeskMessageUrl } from '@/lib/utils/odoo-url'
import { cn } from '@/lib/utils/cn'
import { useEmailThread } from '../api/queries'
import type { EmailMessage } from '../api/types'

export interface EmailThreadPanelProps {
  /** Backend route returning an `EmailThread`. `null` keeps the panel idle. */
  endpoint: string | null
  /** message_index id used to build the "Open in MailDesk" deep link. */
  latestMessageIndexId?: number | null
}

function formatEmailDate(raw: string | null): string {
  if (!raw) return ''
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return raw
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function senderLabel(email: EmailMessage): string {
  return email.sender_display_name || email.from_addr || 'Unknown sender'
}

// Wrap the sanitized email HTML in a minimal, self-contained document. The
// styles below live INSIDE the sandboxed iframe (not the app), so the email's
// own table-based layout / fixed widths / inline images render faithfully and
// can never collapse or escape the page layout. Raw hex here is iframe-local
// email styling, exempt from the app's tokens-only contract.
function buildSrcDoc(bodyHtml: string): string {
  const safe = sanitizeEmailHtml(bodyHtml)
  // color-scheme:light pins the email document to a light rendering context so
  // the browser doesn't apply system dark-mode UA styling to the iframe canvas,
  // and the explicit white background guarantees the email's own near-black
  // inline text colors stay legible regardless of the app/OS theme — every mail
  // client renders message bodies on white for exactly this reason.
  return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank">
<meta name="color-scheme" content="light">
<style>
  :root{color-scheme:light;}
  html,body{margin:0;padding:0;background:#ffffff;}
  body{font:14px/1.55 -apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;
       color:#1f2937;padding:2px 4px;overflow-wrap:break-word;word-break:break-word;}
  img{max-width:100%;height:auto;}
  table{max-width:100%;}
  a{color:#2563eb;}
  blockquote{margin:8px 0;padding-left:12px;border-left:3px solid #e5e7eb;color:#6b7280;}
  p{margin:0 0 8px;} ul,ol{margin:0 0 8px;padding-left:22px;}
</style></head><body>${safe}</body></html>`
}

function resizeEmailFrame(e: React.SyntheticEvent<HTMLIFrameElement>) {
  // sandbox="allow-same-origin" (no allow-scripts) lets us read the rendered
  // height without executing any email script. Grow the frame to its content
  // so the page column scrolls, not the frame (no nested scrollbars).
  const frame = e.currentTarget
  try {
    const h = frame.contentWindow?.document?.documentElement?.scrollHeight
    if (h && h > 0) frame.style.height = `${h + 8}px`
  } catch {
    /* cross-origin read blocked — fall back to the default frame height */
  }
}

function MessageBody({ email }: { email: EmailMessage }) {
  if (email.body_html) {
    return (
      <iframe
        title={`Email from ${senderLabel(email)}`}
        // No allow-scripts: the email cannot run JS. Content is also
        // DOMPurify-sanitized before injection (defense in depth).
        sandbox="allow-same-origin allow-popups"
        srcDoc={buildSrcDoc(email.body_html)}
        onLoad={resizeEmailFrame}
        // bg-gray-0 (fixed white scale token, not theme-swapped) backs the frame
        // during load/resize so there's no dark flash before the white srcDoc paints.
        className="w-full border-0 bg-gray-0"
        style={{ height: '320px' }}
      />
    )
  }
  return (
    <div className="text-role-data-secondary text-secondary">
      {email.preview ? <p className="leading-normal">{email.preview}</p> : null}
      <p className="mt-1 text-xs text-tertiary italic">
        (full message not loaded — open in MailDesk)
      </p>
    </div>
  )
}

export function EmailThreadPanel({
  endpoint,
  latestMessageIndexId,
}: EmailThreadPanelProps) {
  const { data, isLoading, isError } = useEmailThread(endpoint)

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-2 py-6 text-secondary">
        <Loader aria-hidden="true" size={16} className="animate-spin" />
        <span className="text-role-data-secondary">Loading conversation…</span>
      </div>
    )
  }

  // `data.error` is set when the backend couldn't resolve the thread
  // (e.g. the source MailDesk message was unlinked/deleted). Treat it the
  // same as a query error — and NEVER fall through to `data.emails.map`
  // when `emails` is missing or empty, which previously crashed the whole
  // work view with "Cannot read properties of undefined (reading 'map')".
  if (isError || data?.error) {
    return (
      <EmptyState
        variant="error"
        title="Couldn’t load this conversation"
        description="The source email could not be loaded — it may have been moved or removed in MailDesk."
      />
    )
  }

  if (!data || !data.emails || data.emails.length === 0) {
    return (
      <EmptyState
        variant="no-data"
        title="No email on this record"
        description="No MailDesk conversation is linked to this record yet."
      />
    )
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b border-border-subtle bg-surface-sunk px-3 py-2">
        <span className="text-role-section-label text-secondary">
          {data.count} {data.count === 1 ? 'message' : 'messages'}
        </span>
        {latestMessageIndexId != null ? (
          <a
            href={maildeskMessageUrl(latestMessageIndexId)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-role-data-secondary text-accent hover:underline"
          >
            <ExternalLink aria-hidden="true" size={14} />
            Open in MailDesk
          </a>
        ) : null}
      </div>

      <ol className="divide-y divide-border-subtle">
        {data.emails.map((email) => (
          <li key={email.id} className="flex flex-col gap-2 px-3 py-4">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-role-data-primary truncate">
                {senderLabel(email)}
              </span>
              <span className="text-role-metadata whitespace-nowrap text-tertiary">
                {formatEmailDate(email.date)}
              </span>
            </div>
            {email.subject ? (
              <div className="text-role-data-secondary text-secondary">
                {email.subject}
              </div>
            ) : null}
            <MessageBody email={email} />
            {email.has_attachments ? (
              <div
                className={cn(
                  'inline-flex items-center gap-1.5 text-role-metadata text-tertiary',
                )}
              >
                <Paperclip aria-hidden="true" size={13} />
                Attachments
              </div>
            ) : null}
          </li>
        ))}
      </ol>

      {data.truncated ? (
        <p className="border-t border-border-subtle px-3 py-2 text-role-metadata text-tertiary">
          Thread truncated — open in MailDesk to see the full conversation.
        </p>
      ) : null}
    </div>
  )
}
